export function normalizeAgoraJoinUid(uid) {
  const text = String(uid ?? '').trim()
  if (!text) return null

  if (/^\d+$/.test(text)) {
    const numericUid = Number(text)
    if (Number.isSafeInteger(numericUid) && numericUid > 0 && numericUid <= 4294967295) {
      return numericUid
    }
  }

  return text
}

function resolveRtcTokenError(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback
  return payload.message || payload.error || payload.data?.message || payload.data?.error || fallback
}

export async function requestAgoraRtcToken({ tokenServer, channel, role, uid, uidMode, sessionId } = {}) {
  const tokenServerUrl = String(tokenServer || '').trim()
  const channelName = String(channel || '').trim()
  const roleName = String(role || '').trim() || 'audience'
  const requestedUidMode = String(uidMode || '').trim()
  const requestedSessionId = String(sessionId || '').trim()
  const identity = uid == null || String(uid).trim() === '' ? '' : String(uid).trim()

  if (!tokenServerUrl) {
    throw new Error('token server is required')
  }

  if (!channelName) {
    throw new Error('channel is required')
  }

  const search = new URLSearchParams()
  search.set('channel', channelName)
  search.set('role', roleName)
  if (requestedUidMode) search.set('uidMode', requestedUidMode)
  if (requestedSessionId) search.set('sessionId', requestedSessionId)
  if (identity) {
    search.set('uid', identity)
  } else if (!requestedUidMode) {
    search.set('uid', '0')
  }

  const resp = await fetch(`${tokenServerUrl}?${search.toString()}`, {
    credentials: 'omit',
  })

  let payload = null
  let data = null
  try {
    payload = await resp.json()
    data = payload && Object.prototype.hasOwnProperty.call(payload, 'data') ? payload.data : payload
  } catch (error) {
    throw new Error(`rtc token response is not valid JSON (${resp.status})`)
  }

  if (!resp.ok) {
    throw new Error(resolveRtcTokenError(payload, `rtc token request failed (${resp.status})`))
  }

  if (payload && typeof payload === 'object' && payload.code != null && Number(payload.code) !== 0) {
    throw new Error(resolveRtcTokenError(payload, 'rtc token request failed'))
  }

  if (!data?.token) {
    throw new Error('rtc token missing token')
  }

  if (data?.uid == null || String(data.uid).trim() === '') {
    throw new Error('rtc token missing uid')
  }

  if (requestedUidMode === 'uid' && data.uidMode !== 'uid') {
    throw new Error(`rtc token uidMode mismatch: ${data.uidMode || 'unknown'}`)
  }

  return {
    appId: data.appId || '',
    token: data.token,
    uid: data.uid,
    uidMode: data.uidMode || '',
    joinUid: normalizeAgoraJoinUid(data.uid),
    sessionId: data.sessionId || '',
    expireAt: data.expireAt || null,
    channel: data.channel || channelName,
  }
}
